import { isBrowser } from '../shared/browser.js';
import { GLYPH_SCALES, GLYPH_SVGS, INITIAL_COLOR, LETTER_COLORS, MAX_ADDITIONAL_DROPS, SETTINGS } from './contact-letters-config.js';
const CANVAS_SELECTOR = '#letters-canvas';
const TRIGGER_SELECTOR = '[data-wf--icon--variant="random"]';
const OBSTACLE_CONTAINER_SELECTOR = '.w-form, .content__column--form';
const OBSTACLE_ITEM_SELECTOR = 'input, textarea, select, label, h1, h2, h3, h4, h5, h6';
const DESKTOP_OBSTACLE_MEDIA_QUERY = '(min-width: 768px)';
const MOBILE_LETTER_SCALE = 0.5;
const RESET_TRIGGER_CLICK_COUNT = 4;
export const initContactLettersCanvas = () => {
  if (!isBrowser) return;
  const canvas = document.querySelector(CANVAS_SELECTOR);
  const trigger = document.querySelector(TRIGGER_SELECTOR);
  if (!canvas || !trigger || canvas.dataset.lettersCanvasReady === 'true') {
    return;
  }
  const context = canvas.getContext('2d');
  if (!context) return;
  canvas.dataset.lettersCanvasReady = 'true';
  canvas.style.opacity = '1';
  canvas.style.visibility = 'visible';
  canvas.style.pointerEvents = 'none';
  const mouse = { active: false, clientX: 0, clientY: 0, x: 0, y: 0 };
  const letters = [];
  const glyphs = [];
  const state = {
    dpr: 1,
    dropBatchCount: 0,
    frameTime: performance.now(),
    glyphsReady: false,
    height: 0,
    isRunning: false,
    lastTime: performance.now(),
    letterSize: SETTINGS.baseSize,
    nextLetterIndex: 0,
    obstacles: [],
    pendingDrop: false,
    repelRadius: SETTINGS.repelRadius,
    scale: 1,
    triggerClickCount: 0,
    visibleCount: 0,
    width: 0,
  };
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const resolveCssColor = (color) => {
    const match = color.match(/^var\((--[^)]+)\)$/);
    if (!match) return color;
    return (
      getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() ||
      INITIAL_COLOR
    );
  };
  const colorizeSvg = (svg, color) =>
    svg.split('fill="white"').join(`fill="${color}"`);
  const loadGlyphs = () => {
    const resolvedColors = [INITIAL_COLOR, ...LETTER_COLORS.map(resolveCssColor)];
    return Promise.all(
      GLYPH_SVGS.map((svg) =>
        Promise.all(
          resolvedColors.map(
            (color) =>
              new Promise((resolve) => {
                const image = new Image();
                const widthMatch = svg.match(/width="([\d.]+)"/);
                const heightMatch = svg.match(/height="([\d.]+)"/);
                image.onload = () => {
                  resolve({
                    height: Number(heightMatch && heightMatch[1]) || image.height,
                    image,
                    width: Number(widthMatch && widthMatch[1]) || image.width,
                  });
                };
                image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                  colorizeSvg(svg, color)
                )}`;
              })
          )
        )
      )
    );
  };
  const getMaxLetterCount = () =>
    GLYPH_SVGS.length * SETTINGS.letterRepeats * (MAX_ADDITIONAL_DROPS + 1);
  const createLetters = () => {
    letters.length = 0;
    for (let index = 0; index < getMaxLetterCount(); index += 1) {
      letters.push({
        angle: 0,
        colorIndex: 0,
        colorTransitionDuration: SETTINGS.colorTransitionDuration,
        colorTransitionStart: 0,
        fromColorIndex: 0,
        glyphIndex: index % GLYPH_SVGS.length,
        initialized: false,
        radius: 0,
        targetColorIndex: 0,
        vx: 0,
        vy: 0,
        x: 0,
        y: 0,
      });
    }
  };
  const updateMousePosition = () => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = mouse.clientX - rect.left;
    mouse.y = mouse.clientY - rect.top;
    mouse.active =
      mouse.x >= 0 &&
      mouse.x <= rect.width &&
      mouse.y >= 0 &&
      mouse.y <= rect.height;
  };
  const shouldUseObstacles = () => window.matchMedia(DESKTOP_OBSTACLE_MEDIA_QUERY).matches;
  const updateObstacles = () => {
    if (!shouldUseObstacles()) {
      state.obstacles = [];
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const padding = SETTINGS.obstaclePadding;
    const elements = new Set();
    document.querySelectorAll(OBSTACLE_CONTAINER_SELECTOR).forEach((container) => {
      container
        .querySelectorAll(OBSTACLE_ITEM_SELECTOR)
        .forEach((element) => elements.add(element));
    });
    const obstacles = [];
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      obstacles.push({
        left: rect.left - canvasRect.left - padding,
        top: rect.top - canvasRect.top - padding,
        right: rect.right - canvasRect.left + padding,
        bottom: rect.bottom - canvasRect.top + padding,
      });
    });
    state.obstacles = obstacles;
  };
  const getBatchCount = () => GLYPH_SVGS.length * SETTINGS.letterRepeats;
  const getGlyphVariant = (letter, colorIndex) => {
    const variants = glyphs[letter.glyphIndex];
    return variants[colorIndex] || variants[0];
  };
  const getGlyphDrawSize = (letter) => {
    const glyph = getGlyphVariant(letter, letter.colorIndex);
    const glyphScale = GLYPH_SCALES[letter.glyphIndex] || 1;
    const drawHeight = state.letterSize * glyphScale;
    const drawWidth = glyph.width * (drawHeight / glyph.height);
    return { drawHeight, drawWidth };
  };
  const resizeCanvas = () => {
    const rect = canvas.getBoundingClientRect();
    state.width = rect.width;
    state.height = rect.height;
    if (!state.width || !state.height || !glyphs.length) return;
    state.dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = state.width * state.dpr;
    canvas.height = state.height * state.dpr;
    context.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
    state.scale = clamp(
      state.width / SETTINGS.designWidth,
      SETTINGS.minScale,
      SETTINGS.maxScale
    );
    state.letterSize =
      SETTINGS.baseSize * state.scale * (shouldUseObstacles() ? 1 : MOBILE_LETTER_SCALE);
    state.repelRadius = SETTINGS.repelRadius * state.scale;
    updateMousePosition();
    updateObstacles();
  };
  const pickNextColorIndex = (currentIndex) => {
    const colorCount = glyphs[0]?.length || 1;
    if (colorCount <= 2) return 0;
    let nextIndex = 1 + Math.floor(Math.random() * (colorCount - 1));
    if (nextIndex === currentIndex) {
      nextIndex = 1 + (nextIndex % (colorCount - 1));
    }
    return nextIndex;
  };
  const startColorTransition = (letter, targetColorIndex) => {
    letter.fromColorIndex = letter.colorIndex;
    letter.targetColorIndex = targetColorIndex;
    letter.colorTransitionStart = performance.now();
    letter.colorTransitionDuration =
      SETTINGS.colorTransitionDuration + Math.random() * 450;
  };
  const changeExistingLetterColors = () => {
    for (let index = 0; index < state.visibleCount; index += 1) {
      const letter = letters[index];
      if (letter.initialized) startColorTransition(letter, pickNextColorIndex(letter.colorIndex));
    }
  };
  const addLettersFromTop = (animateColor) => {
    const batchCount = getBatchCount();
    const maxCount = letters.length;
    for (let index = 0; index < batchCount; index += 1) {
      const letter = letters[state.nextLetterIndex % maxCount];
      const glyphIndex = index % glyphs.length;
      const progress = batchCount <= 1 ? 0 : index / (batchCount - 1);
      const xNoise = Math.random() - 0.5;
      letter.glyphIndex = glyphIndex;
      letter.colorIndex = 0;
      letter.fromColorIndex = 0;
      letter.targetColorIndex = 0;
      const { drawWidth, drawHeight } = getGlyphDrawSize(letter);
      letter.radius = Math.max(drawWidth, drawHeight) * 0.42;
      letter.boundsRadius = Math.sqrt(drawWidth * drawWidth + drawHeight * drawHeight) * 0.5;
      letter.x = clamp(
        progress * state.width + xNoise * state.letterSize * 0.7,
        letter.boundsRadius,
        state.width - letter.boundsRadius
      );
      letter.y = -state.letterSize - Math.random() * state.height * 0.45;
      letter.vx = (Math.random() - 0.5) * 5;
      letter.vy = Math.random() * 3;
      letter.angle = (Math.random() - 0.5) * 1.1;
      letter.initialized = true;
      if (animateColor) {
        startColorTransition(letter, pickNextColorIndex(0));
      }
      state.nextLetterIndex += 1;
    }
    state.visibleCount = Math.min(state.nextLetterIndex, maxCount);
  };
  const applyCursorRepel = (letter, delta) => {
    if (!mouse.active) return;
    const dx = letter.x - mouse.x;
    const dy = letter.y - mouse.y;
    const distance = Math.sqrt(dx * dx + dy * dy) || 1;
    if (distance > state.repelRadius) return;
    const force = Math.pow(1 - distance / state.repelRadius, 2);
    letter.vx += (dx / distance) * force * SETTINGS.repelForce * delta;
    letter.vy += (dy / distance) * force * SETTINGS.repelForce * delta;
  };
  const resolveSingleObstacleCollision = (letter, obstacle) => {
    const radius = letter.radius * 0.85;
    const closestX = clamp(letter.x, obstacle.left, obstacle.right);
    const closestY = clamp(letter.y, obstacle.top, obstacle.bottom);
    let dx = letter.x - closestX;
    let dy = letter.y - closestY;
    let distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > radius) return;
    if (distance > 0.0001) {
      const nx = dx / distance;
      const ny = dy / distance;
      const overlap = radius - distance;
      letter.x += nx * overlap;
      letter.y += ny * overlap;
      const velocityAlongNormal = letter.vx * nx + letter.vy * ny;
      if (velocityAlongNormal < 0) {
        letter.vx -= nx * velocityAlongNormal * (1 + SETTINGS.bounce);
        letter.vy -= ny * velocityAlongNormal * (1 + SETTINGS.bounce);
        if (Math.abs(ny) > Math.abs(nx)) {
          letter.vx *= SETTINGS.floorFriction;
        }
      }
      return;
    }
    const toLeft = letter.x - obstacle.left;
    const toRight = obstacle.right - letter.x;
    const toTop = letter.y - obstacle.top;
    const toBottom = obstacle.bottom - letter.y;
    const minPenetration = Math.min(toLeft, toRight, toTop, toBottom);
    if (minPenetration === toTop) {
      letter.y = obstacle.top - radius;
      if (letter.vy > 0) letter.vy *= -SETTINGS.bounce;
      letter.vx *= SETTINGS.floorFriction;
    } else if (minPenetration === toBottom) {
      letter.y = obstacle.bottom + radius;
      if (letter.vy < 0) letter.vy *= -SETTINGS.bounce;
    } else if (minPenetration === toLeft) {
      letter.x = obstacle.left - radius;
      if (letter.vx > 0) letter.vx *= -SETTINGS.wallBounce;
    } else {
      letter.x = obstacle.right + radius;
      if (letter.vx < 0) letter.vx *= -SETTINGS.wallBounce;
    }
  };
  const resolveObstacleCollision = (letter) => {
    for (let index = 0; index < state.obstacles.length; index += 1) {
      resolveSingleObstacleCollision(letter, state.obstacles[index]);
    }
  };
  const updateLetter = (letter, delta) => {
    if (!letter.initialized) return;
    applyCursorRepel(letter, delta);
    letter.vy += SETTINGS.gravity * delta;
    letter.vx *= Math.pow(SETTINGS.airFriction, delta);
    letter.vy *= Math.pow(SETTINGS.airFriction, delta);
    letter.x += letter.vx * delta;
    letter.y += letter.vy * delta;
    const boundsRadius = letter.boundsRadius || letter.radius;
    const floor = state.height - boundsRadius;
    const leftWall = boundsRadius;
    const rightWall = state.width - boundsRadius;
    if (letter.y > floor) {
      letter.y = floor;
      letter.vy *= -SETTINGS.bounce;
      letter.vx *= SETTINGS.floorFriction;
      if (Math.abs(letter.vy) < 0.65) letter.vy = 0;
      if (Math.abs(letter.vx) < 0.05) letter.vx = 0;
    }
    if (letter.x < leftWall) {
      letter.x = leftWall;
      letter.vx = Math.abs(letter.vx) * SETTINGS.wallBounce;
      letter.vy *= SETTINGS.floorFriction;
      if (Math.abs(letter.vx) < 0.05) letter.vx = 0;
    }
    if (letter.x > rightWall) {
      letter.x = rightWall;
      letter.vx = -Math.abs(letter.vx) * SETTINGS.wallBounce;
      letter.vy *= SETTINGS.floorFriction;
      if (Math.abs(letter.vx) < 0.05) letter.vx = 0;
    }
    resolveObstacleCollision(letter);
  };
  const solveLetterCollisions = () => {
    for (let index = 0; index < state.visibleCount; index += 1) {
      const first = letters[index];
      if (!first.initialized) continue;
      for (let nextIndex = index + 1; nextIndex < state.visibleCount; nextIndex += 1) {
        const second = letters[nextIndex];
        if (!second.initialized) continue;
        const dx = second.x - first.x;
        const dy = second.y - first.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const minDistance = first.radius + second.radius;
        if (distance >= minDistance) continue;
        const overlap = minDistance - distance;
        const nx = dx / distance;
        const ny = dy / distance;
        const push = overlap * SETTINGS.collisionStrength * 0.5;
        first.x -= nx * push;
        first.y -= ny * push;
        second.x += nx * push;
        second.y += ny * push;
        const impact = (second.vx - first.vx) * nx + (second.vy - first.vy) * ny;
        if (impact < 0) {
          const impulse = -impact * SETTINGS.collisionBounce;
          first.vx -= nx * impulse;
          first.vy -= ny * impulse;
          second.vx += nx * impulse;
          second.vy += ny * impulse;
        }
      }
    }
    for (let index = 0; index < state.visibleCount; index += 1) {
      if (letters[index].initialized) resolveObstacleCollision(letters[index]);
    }
  };
  const drawGlyphVariant = (letter, colorIndex, alpha) => {
    const glyph = getGlyphVariant(letter, colorIndex);
    const glyphScale = GLYPH_SCALES[letter.glyphIndex] || 1;
    const drawHeight = state.letterSize * glyphScale;
    const drawWidth = glyph.width * (drawHeight / glyph.height);
    context.save();
    context.globalAlpha = alpha;
    context.translate(letter.x, letter.y);
    context.rotate(letter.angle);
    context.drawImage(glyph.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    context.restore();
  };
  const drawLetter = (letter) => {
    if (!letter.initialized) return;
    const elapsed = state.frameTime - letter.colorTransitionStart;
    const progress = clamp(elapsed / letter.colorTransitionDuration, 0, 1);
    if (progress >= 1 || letter.fromColorIndex === letter.targetColorIndex) {
      letter.colorIndex = letter.targetColorIndex;
      drawGlyphVariant(letter, letter.colorIndex, 1);
      return;
    }
    drawGlyphVariant(letter, letter.fromColorIndex, 1 - progress);
    drawGlyphVariant(letter, letter.targetColorIndex, progress);
  };
  const animate = (time) => {
    if (!state.isRunning) return;
    state.frameTime = time;
    const delta = Math.min((time - state.lastTime) / 16.666, 2);
    state.lastTime = time;
    updateObstacles();
    context.clearRect(0, 0, state.width, state.height);
    for (let index = 0; index < state.visibleCount; index += 1) {
      updateLetter(letters[index], delta);
    }
    solveLetterCollisions();
    for (let index = 0; index < state.visibleCount; index += 1) {
      drawLetter(letters[index]);
    }
    window.requestAnimationFrame(animate);
  };
  const startAnimation = () => {
    if (state.isRunning) return;
    state.isRunning = true;
    state.lastTime = performance.now();
    window.requestAnimationFrame(animate);
  };
  const dropLetters = (animateColor = true) => {
    if (!state.glyphsReady) {
      state.pendingDrop = true;
      return;
    }
    resizeCanvas();
    if (state.dropBatchCount < MAX_ADDITIONAL_DROPS + 1) {
      addLettersFromTop(animateColor);
      state.dropBatchCount += 1;
    }
    startAnimation();
  };
  const resetLetters = () => {
    state.dropBatchCount = 0;
    state.nextLetterIndex = 0;
    state.triggerClickCount = 0;
    state.visibleCount = 0;
    mouse.active = false;
    state.lastTime = performance.now();
    state.frameTime = state.lastTime;
    letters.forEach((letter) => {
      letter.initialized = false;
      letter.vx = 0;
      letter.vy = 0;
    });
    context.clearRect(0, 0, state.width, state.height);
    dropLetters(false);
  };
  const handleTrigger = () => {
    state.triggerClickCount += 1;
    if (state.triggerClickCount >= RESET_TRIGGER_CLICK_COUNT) {
      resetLetters();
      return;
    }
    changeExistingLetterColors();
    if (state.dropBatchCount < MAX_ADDITIONAL_DROPS + 1) {
      dropLetters(true);
      return;
    }
    startAnimation();
  };
  window.addEventListener(
    'pointermove',
    (event) => {
      mouse.clientX = event.clientX;
      mouse.clientY = event.clientY;
      updateMousePosition();
    },
    { passive: true }
  );
  window.addEventListener('pointerleave', () => {
    mouse.active = false;
  });
  window.addEventListener('scroll', () => {
    updateMousePosition();
    updateObstacles();
  }, { passive: true });
  window.addEventListener('resize', resizeCanvas);
  trigger.addEventListener('click', handleTrigger);
  trigger.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleTrigger();
  });
  if (!['A', 'BUTTON'].includes(trigger.tagName)) {
    trigger.setAttribute('role', 'button');
    if (!trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }
  }
  loadGlyphs().then((loadedGlyphs) => {
    glyphs.push(...loadedGlyphs);
    createLetters();
    resizeCanvas();
    state.glyphsReady = true;
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(resizeCanvas);
      observer.observe(canvas);
      if (shouldUseObstacles()) {
        document
          .querySelectorAll(OBSTACLE_CONTAINER_SELECTOR)
          .forEach((container) => observer.observe(container));
      }
    }
    dropLetters(false);
    if (state.pendingDrop) {
      state.pendingDrop = false;
      handleTrigger();
    }
  });
};
